import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, User, Phone, BookOpen, Copy, Clock, GraduationCap, MessageSquare, Link2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { ClassTrial } from '@/hooks/useClassTrials';
import { CLASS_TRIAL_STATUS_OPTIONS, getTrialStatusColumnColor } from './classTrialStatusColors';
import { cn } from '@/lib/utils';
import { SelectOrderDialog, type SelectedOrderInfo } from './SelectOrderDialog';
import { buildTrialCopyText } from '@/utils/classTrialCopyText';

interface ClassTrialKanbanProps {
  trials: ClassTrial[];
  isLoading: boolean;
  onStatusChange: (
    trial: ClassTrial,
    newStatus: string,
    orderInfo?: { orderId: string; orderCode: string } | null,
  ) => Promise<boolean>;
}

function getTrialFullName(trial: ClassTrial): string {
  const studentDisplay = [trial.student_firstname, trial.student_lastname].filter(Boolean).join(' ');
  return studentDisplay
    ? `${studentDisplay}${trial.student_nickname ? ` (${trial.student_nickname})` : ''}`
    : trial.student_nickname || trial.student_name || 'ไม่ระบุชื่อ';
}

function resolveColumnFromOverId(overId: string, trialsList: ClassTrial[]): string | null {
  if (CLASS_TRIAL_STATUS_OPTIONS.includes(overId)) return overId;
  const overTrial = trialsList.find((t) => t.id === overId);
  if (!overTrial) return null;
  const s = overTrial.status ?? '';
  return CLASS_TRIAL_STATUS_OPTIONS.includes(s) ? s : null;
}

function KanbanColumn({
  status,
  columnClassName,
  title,
  count,
  children,
}: {
  status: string;
  columnClassName: string;
  title: React.ReactNode;
  count: number;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div className={`min-w-[300px] max-w-[340px] flex-shrink-0 bg-muted/30 rounded-lg border border-t-4 ${columnClassName}`}>
      <div className="p-3 border-b">
        <div className="flex items-center justify-between">
          <span className="font-medium text-sm truncate">{title}</span>
          <Badge variant="secondary" className="ml-2 text-xs">
            {count}
          </Badge>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          'p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto min-h-[120px] rounded-b-lg transition-colors',
          isOver && 'bg-muted/60 ring-2 ring-inset ring-primary/25',
        )}
      >
        {children}
      </div>
    </div>
  );
}

function TrialDragPreview({ trial }: { trial: ClassTrial }) {
  const fullName = getTrialFullName(trial);
  return (
    <Card className="cursor-grabbing shadow-lg w-[300px] rotate-1 opacity-[0.97]">
      <CardContent className="p-3">
        <div className="font-medium text-sm leading-tight">{fullName}</div>
      </CardContent>
    </Card>
  );
}

function TrialCard({
  trial,
  isUpdating,
}: {
  trial: ClassTrial;
  isUpdating?: boolean;
}) {
  const navigate = useNavigate();
  const fullName = getTrialFullName(trial);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: trial.id,
    disabled: isUpdating,
  });

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(buildTrialCopyText(trial));
    toast({ title: 'คัดลอกแล้ว' });
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/admin/class-trials/${trial.id}/edit`;
    navigator.clipboard.writeText(url);
    toast({ title: 'คัดลอกลิงก์แล้ว' });
  };

  const dragStyle: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : isUpdating ? 0.6 : undefined,
  };

  return (
    <div ref={setNodeRef} style={dragStyle} {...listeners} {...attributes}>
      <Card
        className={cn(
          'cursor-pointer hover:shadow-md transition-shadow',
          isUpdating && 'pointer-events-none',
        )}
        onClick={() => navigate(`/admin/class-trials/${trial.id}/edit`)}
      >
        <CardContent className="p-3 space-y-1.5">
          {/* Header: name + copy */}
          <div className="flex items-start justify-between gap-1">
            <div className="font-medium text-sm leading-tight">{fullName}</div>
            <div className="flex shrink-0 gap-0.5">
              <button
                type="button"
                onClick={handleCopyLink}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-muted"
                title="คัดลอกลิงก์"
              >
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              <button
                type="button"
                onClick={handleCopy}
                onPointerDown={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-muted"
                title="คัดลอกรายละเอียด"
              >
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Phone */}
          {trial.parent_phone && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="h-3 w-3 shrink-0" />
              <span>ผู้ปกครอง: {trial.parent_phone}</span>
            </div>
          )}
          {trial.student_phone && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Phone className="h-3 w-3 shrink-0" />
              <span>นักเรียน: {trial.student_phone}</span>
            </div>
          )}

          {/* Location */}
          {trial.location && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span>{trial.location}</span>
            </div>
          )}

          {/* Grade + Subject badges */}
          <div className="flex flex-wrap gap-1">
            {trial.grade && (
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                <GraduationCap className="h-3 w-3 mr-0.5" />{trial.grade}
              </Badge>
            )}
            {trial.subject && (
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                <BookOpen className="h-3 w-3 mr-0.5" />{trial.subject}
              </Badge>
            )}
          </div>

          {/* Requested trial date/time */}
          {trial.class_date && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3 shrink-0" />
              <span>นัด: {trial.class_date}{trial.trial_time ? ` ${trial.trial_time}` : ''}</span>
            </div>
          )}

          {/* Actual trial date/time */}
          {trial.actual_trial_day && (
            <div className="flex items-center gap-1.5 text-xs text-blue-600">
              <Clock className="h-3 w-3 shrink-0" />
              <span>จริง: {trial.actual_trial_day}{trial.actual_trial_time ? ` ${trial.actual_trial_time}` : ''}</span>
            </div>
          )}

          {/* Desired course - text display */}
          {trial.desired_course && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <BookOpen className="h-3 w-3 shrink-0" />
              <span>คอร์ส: {trial.desired_course}</span>
            </div>
          )}

          {/* Teacher */}
          {trial.teacher_code && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <User className="h-3 w-3 shrink-0" />
              <span>{trial.teacher_code}</span>
            </div>
          )}

          {/* Parent expectations */}
          {trial.parent_expectations_trial && (
            <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <MessageSquare className="h-3 w-3 shrink-0 mt-0.5" />
              <span className="line-clamp-2">{trial.parent_expectations_trial}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ClassTrialKanban({ trials, isLoading, onStatusChange }: ClassTrialKanbanProps) {
  const [activeTrial, setActiveTrial] = useState<ClassTrial | null>(null);
  const [updatingTrialId, setUpdatingTrialId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{ trial: ClassTrial; targetColumn: string } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const groupedTrials = useMemo(() => {
    const grouped: Record<string, ClassTrial[]> = {};
    CLASS_TRIAL_STATUS_OPTIONS.forEach((status) => {
      grouped[status] = [];
    });
    const otherTrials: ClassTrial[] = [];
    trials.forEach((trial) => {
      const status = trial.status || '';
      if (grouped[status]) {
        grouped[status].push(trial);
      } else {
        otherTrials.push(trial);
      }
    });
    return { grouped, otherTrials };
  }, [trials]);

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const trial = trials.find((t) => t.id === id);
    setActiveTrial(trial ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTrial(null);
    if (!over) return;

    const trial = trials.find((t) => t.id === String(active.id));
    if (!trial) return;

    const targetColumn = resolveColumnFromOverId(String(over.id), trials);
    if (!targetColumn || targetColumn === (trial.status || '')) return;

    if (targetColumn === 'ปิดการขายเรียบร้อย') {
      setPendingMove({ trial, targetColumn });
      return;
    }

    setUpdatingTrialId(trial.id);
    try {
      await onStatusChange(trial, targetColumn);
    } finally {
      setUpdatingTrialId(null);
    }
  };

  const handleConfirmOrder = async (info: SelectedOrderInfo) => {
    if (!pendingMove) return;
    const { trial, targetColumn } = pendingMove;
    setPendingMove(null);
    setUpdatingTrialId(trial.id);
    try {
      await onStatusChange(trial, targetColumn, info);
    } finally {
      setUpdatingTrialId(null);
    }
  };

  const handleDragCancel = () => {
    setActiveTrial(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const { grouped, otherTrials } = groupedTrials;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-4 overflow-x-auto pb-4">
        {CLASS_TRIAL_STATUS_OPTIONS.map((status) => {
          const statusTrials = grouped[status];
          return (
            <KanbanColumn
              key={status}
              status={status}
              columnClassName={getTrialStatusColumnColor(status)}
              title={status}
              count={statusTrials.length}
            >
              {statusTrials.length === 0 && (
                <div className="text-center py-6 text-muted-foreground text-xs pointer-events-none">
                  ไม่มีรายการ
                </div>
              )}
              {statusTrials.map((trial) => (
                <TrialCard key={trial.id} trial={trial} isUpdating={updatingTrialId === trial.id} />
              ))}
            </KanbanColumn>
          );
        })}

        {otherTrials.length > 0 && (
          <div className="min-w-[300px] max-w-[340px] flex-shrink-0 bg-muted/30 rounded-lg border border-t-4 border-t-muted">
            <div className="p-3 border-b">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">อื่นๆ</span>
                <Badge variant="secondary" className="ml-2 text-xs">{otherTrials.length}</Badge>
              </div>
            </div>
            <div className="p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto">
              {otherTrials.map((trial) => (
                <TrialCard key={trial.id} trial={trial} isUpdating={updatingTrialId === trial.id} />
              ))}
            </div>
          </div>
        )}
      </div>

      <DragOverlay dropAnimation={null}>{activeTrial ? <TrialDragPreview trial={activeTrial} /> : null}</DragOverlay>
      <SelectOrderDialog
        open={!!pendingMove}
        onOpenChange={(o) => { if (!o) setPendingMove(null); }}
        onConfirm={handleConfirmOrder}
        onCancel={() => setPendingMove(null)}
      />
    </DndContext>
  );
}
